import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { timeout } from 'rxjs';
import { APP_CONSTANTS } from '../constants/app.constants';

// Data Interfaces
export interface SpotifyUserData {
  id: string;
  display_name: string;
  email: string;
  images: Array<{ url: string; height: number; width: number }>;
  followers: { total: number };
  country: string;
}

export interface SpotifyTrack {
  id: string;
  name: string;
  artists: Array<{ name: string; id: string }>;
  album: { name: string; images: Array<{ url: string }> };
  duration_ms: number;
  preview_url?: string;
}

export interface SpotifyPlaylist {
  id: string;
  name: string;
  description: string;
  tracks: { total: number };
  images: Array<{ url: string }>;
  owner: { display_name: string };
}

export interface DiscogsUserData {
  id: number;
  username: string;
  name: string;
  email: string;
  avatar_url?: string;
  num_collection: number;
  num_wantlist: number;
}

export interface DiscogsItem {
  id: number;
  basic_information: {
    title: string;
    artists: Array<{ name: string }>;
    year: number;
    thumb: string;
  };
  date_added: string;
}

// Spotify Library Sync Debug Interface
export interface SpotifyLibrarySyncDebugResponse {
  user: SpotifyUserData;
  stats: {
    totalLikedTracks: number;
    totalSavedAlbums: number;
    totalPlaylists: number;
    totalUniqueArtists: number;
    syncDurationMs: number;
  };
  likedTracks: SpotifyTrack[];
  savedAlbums: Array<{
    id: string;
    name: string;
    artists: Array<{ name: string }>;
    release_date: string;
    images: Array<{ url: string }>;
  }>;
  playlists: SpotifyPlaylist[];
  debug: {
    spotifyApiCalls: number;
    rateLimitEncountered: boolean;
    timestamp: string;
    [key: string]: any;
  };
  // Optional properties for handling large datasets
  _truncated?: boolean;
  _statsOnly?: boolean;
  _message?: string;
  _originalCounts?: {
    likedTracks: number;
    savedAlbums: number;
    playlists: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class MusicSyncService {
  private http = inject(HttpClient);

  // Spotify Signals
  readonly spotifyConnected = signal<boolean>(false);
  readonly spotifyLoading = signal<boolean>(false);
  readonly spotifyUser = signal<SpotifyUserData | null>(null);
  readonly spotifyError = signal<string | null>(null);

  // Discogs Signals
  readonly discogsConnected = signal<boolean>(false);
  readonly discogsLoading = signal<boolean>(false);
  readonly discogsUser = signal<DiscogsUserData | null>(null);
  readonly discogsWishlist = signal<DiscogsItem[]>([]);
  readonly discogsError = signal<string | null>(null);

  // Spotify Library Sync Debug Signals
  readonly spotifyLibrarySyncLoading = signal<boolean>(false);
  readonly spotifyLibrarySyncData = signal<SpotifyLibrarySyncDebugResponse | null>(null);
  readonly spotifyLibrarySyncError = signal<string | null>(null);
  readonly spotifyLibrarySyncProgress = signal<number>(0);
  readonly spotifyLibrarySyncPhase = signal<string>('');
  readonly spotifyLibrarySyncCurrent = signal<number>(0);
  readonly spotifyLibrarySyncTotal = signal<number>(0);
  readonly spotifyLibrarySyncSessionId = signal<string | null>(null);
  readonly spotifyLibrarySyncCancel = signal<(() => void) | null>(null);
  readonly spotifyLibrarySyncPhaseType = signal<string>('');

  // Computed Signals
  readonly bothConnected = computed(() =>
    this.spotifyConnected() && this.discogsConnected()
  );

  readonly connectionProgress = computed(() => {
    const spotify = this.spotifyConnected() ? 50 : 0;
    const discogs = this.discogsConnected() ? 50 : 0;
    return spotify + discogs;
  });

  readonly isAnyLoading = computed(() =>
    this.spotifyLoading() || this.discogsLoading()
  );

  readonly connectionStatus = computed(() => {
    const spotify = this.spotifyConnected();
    const discogs = this.discogsConnected();

    if (spotify && discogs) {
      return APP_CONSTANTS.TEXTS.STATUS_MESSAGES.BOTH_CONNECTED;
    } else if (spotify || discogs) {
      return APP_CONSTANTS.TEXTS.STATUS_MESSAGES.ONE_CONNECTED;
    } else {
      return APP_CONSTANTS.TEXTS.STATUS_MESSAGES.NONE_CONNECTED;
    }
  });

  constructor() {
    this.initializeFromStorage();
  }

  // Initialize connections from localStorage
  private initializeFromStorage(): void {
    const spotifyToken = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.SPOTIFY_TOKEN);
    const discogsToken = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.DISCOGS_TOKEN);

    if (spotifyToken) {
      this.validateAndFetchSpotifyData();
    }

    if (discogsToken) {
      // Simply mark as connected without fetching data - user info will be available after proper login
      this.discogsConnected.set(true);
      console.log('✅ MusicSync: Discogs token found in localStorage, marked as connected');
    }
  }

    // OAuth Connection Methods
  connectSpotify(): void {
    console.log('🎵 MusicSync: Initiating Spotify connection...');
    console.log('🎵 MusicSync: Clearing any previous errors');

    // Reset states
    this.spotifyError.set(null);
    this.spotifyLoading.set(true);
    this.spotifyConnected.set(false);

    // Clear any existing tokens
    localStorage.removeItem(APP_CONSTANTS.STORAGE_KEYS.SPOTIFY_TOKEN);

    try {
      // Generate state parameter for security
      const state = Math.random().toString(36).substring(7);
      sessionStorage.setItem('spotify_auth_state', state);

      // Build the auth URL with required parameters
      const authUrl = new URL(`${APP_CONSTANTS.API.BASE_URL}${APP_CONSTANTS.API.ENDPOINTS.SPOTIFY_AUTH}`);
      authUrl.searchParams.append('type', 'connect');
      authUrl.searchParams.append('state', state);
      authUrl.searchParams.append('redirect_uri', `${window.location.origin}${APP_CONSTANTS.ROUTES.SPOTIFY_CONNECT_CALLBACK}`);

      console.log('🎵 MusicSync: Redirecting to:', authUrl.toString());

      // Redirect to Spotify auth
      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('❌ MusicSync: Error initiating Spotify connection:', error);
      this.spotifyError.set('Failed to initiate Spotify connection');
      this.spotifyLoading.set(false);
    }
  }

  async connectDiscogs(): Promise<void> {
    try {
      const authUrl = `${APP_CONSTANTS.API.BASE_URL}/auth/discogs`;
      const popup = window.open(authUrl, 'DiscogsAuth', 'width=600,height=700');
      if (!popup) {
        throw new Error('Could not open Discogs authentication window');
      }

      const popupPromise = new Promise<{ token: string; tokenSecret: string }>((resolve, reject) => {
        const messageListener = (event: MessageEvent) => {
          if (event.origin !== window.location.origin && !event.origin.includes('discogs')) return;
          if (event.data && event.data.type === 'auth_success' && event.data.data) {
            window.removeEventListener('message', messageListener);
            resolve(event.data.data);
          }
        };
        window.addEventListener('message', messageListener);
        const popupCheck = setInterval(() => {
          if (popup.closed) {
            clearInterval(popupCheck);
            window.removeEventListener('message', messageListener);
            reject(new Error('Discogs authentication window was closed'));
          }
        }, 500);
      });

      const { token, tokenSecret } = await popupPromise;
      localStorage.setItem(APP_CONSTANTS.STORAGE_KEYS.DISCOGS_TOKEN, token);
      localStorage.setItem('discogs_tokenSecret', tokenSecret);

      // Mark as connected - user data should come from the auth callback
      this.discogsConnected.set(true);
      console.log('✅ MusicSync: Discogs tokens stored, marked as connected');

    } catch (error: any) {
      this.discogsError.set('Failed to start Discogs connection');
    }
  }

  async handleOAuthCallback(provider: 'spotify' | 'discogs', token: string, verifier?: string): Promise<void> {
    console.log(`🔄 MusicSync: Starting handleOAuthCallback for ${provider} with token: ${token?.substring(0, 20)}...`);

    // 🚨 DEBUG: Analyze the token type
    console.log(`🔍 TOKEN ANALYSIS for ${provider}:`);
    console.log(`🔍 Token length: ${token?.length}`);
    console.log(`🔍 Token starts with: ${token?.substring(0, 10)}...`);
    console.log(`🔍 Token ends with: ...${token?.substring(token.length - 10)}`);
    console.log(`🔍 Is JWT? ${token?.startsWith('ey')}`);
    console.log(`🔍 Contains dots? ${token?.includes('.')}`);
    console.log(`🔍 Dot count: ${(token?.match(/\./g) || []).length}`);

    try {
      // Validate token format and existence
      if (!token) {
        throw new Error('No token provided');
      }

      // For Spotify, ensure we're getting a proper Spotify access token
      if (provider === 'spotify') {
        if (token.startsWith('ey')) {
          console.error('❌ MusicSync: Received JWT instead of Spotify access token');
          this.spotifyError.set('Invalid token format received. Please try again.');
          throw new Error('Invalid token format: Received JWT instead of Spotify access token');
        }
      }

      // Activate loading state
      if (provider === 'spotify') {
        this.spotifyLoading.set(true);
        this.spotifyError.set(null);
      } else {
        this.discogsLoading.set(true);
        this.discogsError.set(null);
      }

      // Store token
      const storageKey = provider === 'spotify'
        ? APP_CONSTANTS.STORAGE_KEYS.SPOTIFY_TOKEN
        : APP_CONSTANTS.STORAGE_KEYS.DISCOGS_TOKEN;

      localStorage.setItem(storageKey, token);
      console.log(`✅ MusicSync: Token stored in localStorage with key: ${storageKey}`);
      console.log(`🔍 Stored token preview: ${token?.substring(0, 30)}...`);

      // Fetch provider-specific data
      if (provider === 'spotify') {
        await this.validateAndFetchSpotifyData();
      } else {
        this.discogsConnected.set(true);
        this.discogsLoading.set(false);
      }

    } catch (error: any) {
      console.error(`❌ MusicSync: OAuth callback failed for ${provider}:`, error);
      if (provider === 'spotify') {
        this.spotifyLoading.set(false);
        this.spotifyError.set(error.message || 'Failed to connect to Spotify');
      } else {
        this.discogsLoading.set(false);
        this.discogsError.set(error.message || 'Failed to connect to Discogs');
      }
      throw error;
    }
  }

  // Spotify Data Fetching
  private async validateAndFetchSpotifyData(): Promise<void> {
    const token = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.SPOTIFY_TOKEN);
    console.log('🎵 MusicSync: Validating and fetching Spotify data');
    console.log('🎵 MusicSync: Spotify token exists:', !!token);

    if (!token) {
      console.log('🎵 MusicSync: No Spotify token found, skipping data fetch');
      return;
    }

    console.log('🎵 MusicSync: Setting Spotify loading to TRUE (data fetch)');
    this.spotifyLoading.set(true);
    this.spotifyError.set(null);

    try {
      const baseUrl = APP_CONSTANTS.API.BASE_URL;
      const apiUrl = `${baseUrl}/spotify/me`;

      console.log('🎵 MusicSync: Making API call to:', apiUrl);

      // Validate token type for debugging
      if (token?.startsWith('ey')) {
        console.log('🚨 MusicSync: Warning - Received JWT token instead of Spotify access token');
      }

            // Fetch user profile (using Spotify token from interceptor)
      const userResponse = await this.http.get<SpotifyUserData>(apiUrl).toPromise();
      this.spotifyUser.set(userResponse || null);

      this.spotifyConnected.set(true);
      console.log('✅ MusicSync: Spotify data loaded successfully');
      console.log(`📊 MusicSync: Spotify - Profile: ${userResponse?.display_name}`);
      console.log('✅ MusicSync: Setting Spotify connected to TRUE');

    } catch (error: any) {
      console.error('❌ MusicSync: Error fetching Spotify data:', error);
      console.error('❌ MusicSync: Error status:', error.status);
      console.error('❌ MusicSync: Error message:', error.message);
      console.error('❌ MusicSync: Error details:', error.error);
      console.error('❌ MusicSync: Full error object:', JSON.stringify(error, null, 2));

      const errorMessage = error.error?.message || error.message || 'Failed to load Spotify data';
      this.spotifyError.set(`Error ${error.status || 'Unknown'}: ${errorMessage}`);
      this.disconnectSpotify();
    } finally {
      console.log('🎵 MusicSync: Setting Spotify loading to FALSE (data fetch complete)');
      this.spotifyLoading.set(false);
    }
  }

  // Discogs Data Fetching
  private async validateAndFetchDiscogsData(): Promise<void> {
    const token = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.DISCOGS_TOKEN);
    console.log('🎧 MusicSync: validateAndFetchDiscogsData called, token exists:', !!token);
    if (!token) {
      console.log('🎧 MusicSync: No Discogs token found, skipping data fetch');
      return;
    }

    console.log('🎧 MusicSync: Setting Discogs loading to TRUE (data fetch)');
    this.discogsLoading.set(true);
    this.discogsError.set(null);

    try {
      const baseUrl = APP_CONSTANTS.API.BASE_URL;

      // Fetch user profile (using JWT token from interceptor)
      const response = await this.http.get<DiscogsUserData>(`${baseUrl}${APP_CONSTANTS.API.ENDPOINTS.DISCOGS_PROFILE}`, {
        observe: 'response'
      }).toPromise();

      if (response?.status === 200) {
        // Success case (200 OK)
        const userData = response.body;
        this.discogsUser.set(userData || null);
        console.log('✅ MusicSync: Discogs profile loaded successfully');
        console.log(`📊 MusicSync: Discogs - Profile: ${userData?.username}`);

        // Try to fetch wishlist (optional endpoint)
        try {
          const wishlistResponse = await this.http.get<{ wants: DiscogsItem[] }>(`${baseUrl}${APP_CONSTANTS.API.ENDPOINTS.DISCOGS_WISHLIST}`).toPromise();
          this.discogsWishlist.set(wishlistResponse?.wants || []);
          console.log('✅ MusicSync: Discogs wishlist loaded successfully');
        } catch (error: any) {
          console.log('⚠️ MusicSync: Wishlist not available yet (404) - skipping');
          this.discogsWishlist.set([]);
        }

        this.discogsConnected.set(true);
        console.log('✅ MusicSync: Setting Discogs connected to TRUE');

      } else if (response?.status === 400) {
        // No Discogs account connected (400)
        console.log('⚠️ MusicSync: No Discogs account connected');
        this.discogsError.set('No Discogs account connected. Please connect your Discogs account to continue.');
        this.disconnectDiscogs();
      } else if (response?.status === 500) {
        // Server error (500)
        console.error('❌ MusicSync: Server error while fetching Discogs data');
        this.discogsError.set('Unable to connect to Discogs at this time. Please try again later.');
        this.disconnectDiscogs();
      } else {
        // Unexpected response
        console.error('❌ MusicSync: Unexpected response while fetching Discogs data:', response?.status);
        this.discogsError.set('Unexpected error while connecting to Discogs. Please try again.');
        this.disconnectDiscogs();
      }

    } catch (error: any) {
      console.error('❌ MusicSync: Error fetching Discogs data:', error);

      // Handle specific error cases
      if (error.status === 400) {
        this.discogsError.set('No Discogs account connected. Please connect your Discogs account to continue.');
      } else if (error.status === 500) {
        this.discogsError.set('Unable to connect to Discogs at this time. Please try again later.');
      } else {
        this.discogsError.set('Failed to load Discogs data. Please try again.');
      }

      this.disconnectDiscogs();
    } finally {
      console.log('🎧 MusicSync: Setting Discogs loading to FALSE (data fetch complete)');
      this.discogsLoading.set(false);
    }
  }

  // Spotify Library Sync Debug Method
  async syncSpotifyLibraryDebug(): Promise<void> {
    const token = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.SPOTIFY_TOKEN);
    console.log('🎵 MusicSync: Starting Spotify library sync debug with SSE');

    // 🚨 DEBUG: Check stored token before starting
    console.log('🔍 SPOTIFY TOKEN CHECK:');
    console.log('🔍 Token exists in localStorage:', !!token);
    if (token) {
      console.log('🔍 Token length:', token.length);
      console.log('🔍 Token preview:', token.substring(0, 50) + '...');
      console.log('🔍 Is JWT?', token.startsWith('ey'));
      console.log('🔍 Contains dots?', token.includes('.'));
      console.log('🔍 Storage key used:', APP_CONSTANTS.STORAGE_KEYS.SPOTIFY_TOKEN);
    } else {
      console.log('🔍 No token found in localStorage');
      console.log('🔍 All localStorage keys:', Object.keys(localStorage));
    }

    if (!token) {
      const errorMessage = 'No Spotify token found - please connect your Spotify account first';
      console.error('❌ MusicSync:', errorMessage);
      this.spotifyLibrarySyncError.set(errorMessage);
      return;
    }

    if (!this.spotifyConnected()) {
      const errorMessage = 'Spotify not connected - please connect your account first';
      console.error('❌ MusicSync:', errorMessage);
      this.spotifyLibrarySyncError.set(errorMessage);
      return;
    }

    console.log('🎵 MusicSync: Setting library sync loading to TRUE');
    this.spotifyLibrarySyncLoading.set(true);
    this.spotifyLibrarySyncError.set(null);
    this.spotifyLibrarySyncProgress.set(0);
    this.spotifyLibrarySyncPhase.set('Connecting to stream...');
    this.spotifyLibrarySyncPhaseType.set('init');
    this.spotifyLibrarySyncCurrent.set(0);
    this.spotifyLibrarySyncTotal.set(0);

    return new Promise((resolve, reject) => {
      try {
        const baseUrl = APP_CONSTANTS.API.BASE_URL;

        // Create SSE endpoint URL with stats-only mode to prevent browser crashes with large libraries
        const streamUrl = `${baseUrl}/spotify/sync-library/stream?token=${encodeURIComponent(token)}&debug=false&includeAudioFeatures=false&statsOnly=true`;

        console.log('🎵 MusicSync: Connecting to SSE stream with STATS-ONLY mode for large library');
        console.log('🎵 MusicSync: URL:', streamUrl);
        console.log('🎵 MusicSync: This prevents browser crashes with large datasets (7000+ tracks)');

        const eventSource = new EventSource(streamUrl);
        let sessionId: string | null = null;

        // Store cancel function
        const cancelFunction = () => {
          console.log('🛑 MusicSync: Sync cancelled by user');
          eventSource.close();
          this.spotifyLibrarySyncLoading.set(false);
          this.spotifyLibrarySyncPhase.set('Sync cancelled');
          this.spotifyLibrarySyncCancel.set(null);
          reject(new Error('Sync cancelled by user'));
        };
        this.spotifyLibrarySyncCancel.set(cancelFunction);

        eventSource.onopen = () => {
          console.log('✅ MusicSync: SSE connection opened');
          this.spotifyLibrarySyncPhase.set('Connected to sync stream');
        };

        eventSource.onmessage = (event) => {
          try {
            console.log('📨 MusicSync: Received raw event, size:', event.data.length, 'characters');

            // Handle very large JSON payloads safely
            let data;
            try {
              data = JSON.parse(event.data);
            } catch (jsonError) {
              console.error('❌ MusicSync: JSON parse error for large payload:', jsonError);
              console.log('📊 MusicSync: Payload preview:', event.data.substring(0, 200) + '...');
              return; // Skip this event if JSON parsing fails
            }

            console.log('📨 MusicSync: Progress update:', {
              type: data.type,
              phase: data.phase,
              percentage: data.percentage,
              current: data.current,
              total: data.total,
              hasData: !!data.data,
              dataSize: data.data ? 'Large object' : 'No data'
            });

            // Store session ID
            if (data.sessionId && !sessionId) {
              sessionId = data.sessionId;
              this.spotifyLibrarySyncSessionId.set(sessionId);
              console.log('🆔 MusicSync: Session ID:', sessionId);
            }

            // Update progress signals
            this.spotifyLibrarySyncProgress.set(data.percentage || 0);
            this.spotifyLibrarySyncPhase.set(data.phase || '');
            this.spotifyLibrarySyncPhaseType.set(data.type || '');
            this.spotifyLibrarySyncCurrent.set(data.current || 0);
            this.spotifyLibrarySyncTotal.set(data.total || 0);

                        // Handle different event types
            if (data.type === 'complete') {
              console.log('🎉 MusicSync: Sync completed successfully');

              try {
                this.spotifyLibrarySyncProgress.set(100);
                this.spotifyLibrarySyncPhase.set('Sync completed successfully!');

                // In stats-only mode, we only receive essential data (no large arrays)
                if (data.data) {
                  console.log('📊 MusicSync: Received stats-only data');
                  console.log('📊 MusicSync: Data contains:', Object.keys(data.data));

                  // Create safe summary data for stats-only mode
                  const statsOnlyData = {
                    user: data.data.user || {},
                    stats: data.data.stats || {},
                    debug: data.data.debug || {},
                    // Empty arrays since we're in stats-only mode
                    likedTracks: [],
                    savedAlbums: [],
                    playlists: [],
                    _statsOnly: true,
                    _message: 'Complete sync successful! Only statistics shown to prevent browser crashes with large libraries.'
                  };

                  this.spotifyLibrarySyncData.set(statsOnlyData);
                  console.log('✅ MusicSync: Stats-only data set successfully');
                } else {
                  console.log('📊 MusicSync: No data received, sync completed with progress only');
                }

                // Close connection
                eventSource.close();
                this.spotifyLibrarySyncLoading.set(false);
                this.spotifyLibrarySyncCancel.set(null);
                resolve(data.data || { stats: 'Sync completed successfully' });

              } catch (completeError) {
                console.error('❌ MusicSync: Error processing complete event:', completeError);
                this.spotifyLibrarySyncError.set('Sync completed but failed to process final data');
                eventSource.close();
                this.spotifyLibrarySyncLoading.set(false);
                this.spotifyLibrarySyncCancel.set(null);
                reject(completeError);
              }

            } else if (data.type === 'error') {
              console.error('❌ MusicSync: Sync error from stream:', data.error);
              this.spotifyLibrarySyncError.set(data.error || 'Unknown error occurred');
              this.spotifyLibrarySyncPhase.set('Sync failed');

              eventSource.close();
              this.spotifyLibrarySyncLoading.set(false);
              this.spotifyLibrarySyncCancel.set(null);
              reject(new Error(data.error || 'Sync failed'));
            }

          } catch (parseError) {
            console.error('❌ MusicSync: Error parsing SSE data:', parseError, event.data);
          }
        };

        eventSource.onerror = (error) => {
          console.error('❌ MusicSync: SSE connection error:', error);

          let errorMessage = 'Connection to sync stream failed';

          // Check if this is a timeout or network issue
          if (eventSource.readyState === EventSource.CLOSED) {
            errorMessage = 'Sync stream closed unexpectedly - large libraries (7000+ tracks) can take up to 10 minutes';
          } else if (eventSource.readyState === EventSource.CONNECTING) {
            errorMessage = 'Unable to connect to sync stream - check your connection';
          }

          this.spotifyLibrarySyncError.set(errorMessage);
          this.spotifyLibrarySyncPhase.set('Connection failed');
          this.spotifyLibrarySyncLoading.set(false);
          this.spotifyLibrarySyncCancel.set(null);

          eventSource.close();
          reject(new Error(errorMessage));
        };

        // Set a maximum timeout (15 minutes for very large libraries)
        setTimeout(() => {
          if (eventSource.readyState !== EventSource.CLOSED) {
            console.log('⏰ MusicSync: Sync timeout after 15 minutes');
            eventSource.close();
            this.spotifyLibrarySyncError.set('Sync timed out after 15 minutes - this is unusual for even very large libraries');
            this.spotifyLibrarySyncLoading.set(false);
            this.spotifyLibrarySyncCancel.set(null);
            reject(new Error('Sync timeout'));
          }
        }, 900000); // 15 minutes

      } catch (error: any) {
        console.error('❌ MusicSync: Error setting up SSE connection:', error);
        this.spotifyLibrarySyncError.set('Failed to start sync stream');
        this.spotifyLibrarySyncLoading.set(false);
        reject(error);
      }
    });
  }

  // Disconnect Methods
  disconnectSpotify(): void {
    localStorage.removeItem(APP_CONSTANTS.STORAGE_KEYS.SPOTIFY_TOKEN);
    this.spotifyConnected.set(false);
    this.spotifyUser.set(null);
    console.log('🔐 Spotify disconnected');
  }

  disconnectDiscogs(): void {
    localStorage.removeItem(APP_CONSTANTS.STORAGE_KEYS.DISCOGS_TOKEN);
    this.discogsConnected.set(false);
    this.discogsUser.set(null);
    this.discogsWishlist.set([]);
    this.discogsError.set(null);
    console.log('🔐 Discogs disconnected');
  }

  // Refresh data (useful for manual refresh)
  async refreshSpotifyData(): Promise<void> {
    if (this.spotifyConnected()) {
      await this.validateAndFetchSpotifyData();
    }
  }

  async refreshDiscogsData(): Promise<void> {
    // No refresh needed for Discogs - user data comes from OAuth callback
    console.log('🎧 MusicSync: Discogs refresh not needed - data comes from OAuth callback');
  }

  // Debug method
  getDebugData() {
    return {
      spotify: {
        connected: this.spotifyConnected(),
        loading: this.spotifyLoading(),
        user: this.spotifyUser(),
        error: this.spotifyError(),
        librarySync: {
          loading: this.spotifyLibrarySyncLoading(),
          data: this.spotifyLibrarySyncData(),
          error: this.spotifyLibrarySyncError()
        }
      },
      discogs: {
        connected: this.discogsConnected(),
        loading: this.discogsLoading(),
        user: this.discogsUser(),
        wishlistCount: this.discogsWishlist().length,
        error: this.discogsError()
      },
      computed: {
        bothConnected: this.bothConnected(),
        progress: this.connectionProgress(),
        isAnyLoading: this.isAnyLoading(),
        status: this.connectionStatus()
      }
    };
  }

  // New function to handle Discogs OAuth callback
  async handleDiscogsCallback(callbackToken: string): Promise<void> {
    const lastToken = localStorage.getItem('discogs_request_token');
    if (lastToken && callbackToken !== lastToken) {
      this.discogsError.set('Token mismatch: received token does not match the last generated one. Please try logging in again.');
      localStorage.removeItem('discogs_oauth_in_progress');
      localStorage.removeItem('discogs_request_token');
      return;
    }
    // If everything is OK, clear flags and continue with normal flow
    localStorage.removeItem('discogs_oauth_in_progress');
    localStorage.removeItem('discogs_request_token');
    // Here you can continue with the normal flow, for example:
    // await this.handleOAuthCallback('discogs', callbackToken);
  }
}
